import {Button, Loader} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {useCallback, useEffect, useState} from 'react';
import {MdCheckCircleOutline, MdControlPoint} from 'react-icons/md';
import {
  COLOR_AI_GRADIENT_START,
  COLOR_WHITE,
} from '../../../../config/constants/colorCodes';
import {
  useDeleteFollowCompany,
  useUpdateFollowCompany,
} from '../../../../core/repositories/companiesRepository';
import {useAuth} from '../../../../core/usecases/useAuth';
import {cn} from '../../../../lib/classname';
import {NoLoginAlertModal} from '../../../../shared/components/NoLoginAlertModal';
import {getAIGradient} from '../../usecase/getAIGradient';

type Props = {
  companyId: number;
  isFollowed: boolean;
  followCount: number;
  onFollowChange?: (isFollowed: boolean, followCount: number) => void;
  customFollowText?: string;
};

export const CustomFollowCompanyButton: React.FC<Props> = ({
  companyId,
  isFollowed: externalIsFollowed,
  followCount: externalFollowCount,
  onFollowChange,
  customFollowText,
}) => {
  const [isFollowed, setIsFollowed] = useState<boolean>(externalIsFollowed);
  const [followCount, setFollowCount] = useState<number>(externalFollowCount);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoginOpened, {open: openLoginModal, close: closeLoginModal}] =
    useDisclosure(false);
  const {isSignedIn} = useAuth();

  useEffect(() => {
    setIsFollowed(externalIsFollowed);
  }, [externalIsFollowed]);

  useEffect(() => {
    setFollowCount(externalFollowCount);
  }, [externalFollowCount]);

  const updateFollowCompany = useUpdateFollowCompany({
    onSuccessFunc({followCompanyCount}) {
      setIsFollowed(true);
      setFollowCount(followCompanyCount);
      setIsLoading(false);

      if (onFollowChange) {
        onFollowChange(true, followCompanyCount);
      }
    },
  });

  const deleteFollowCompany = useDeleteFollowCompany({
    onSuccessFunc({followCompanyCount}) {
      setIsFollowed(false);
      setFollowCount(followCompanyCount);
      setIsLoading(false);

      if (onFollowChange) {
        onFollowChange(false, followCompanyCount);
      }
    },
  });

  const handleFollowCompany = useCallback(async () => {
    if (!isSignedIn) {
      openLoginModal();
      return;
    }

    setIsLoading(true);

    if (isFollowed) {
      deleteFollowCompany.mutate({companyId});
    } else {
      updateFollowCompany.mutate({companyId});
    }
  }, [
    companyId,
    isFollowed,
    isSignedIn,
    deleteFollowCompany,
    updateFollowCompany,
    openLoginModal,
  ]);

  const gradientTextStyle = {
    background: getAIGradient(),
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };

  const noHoverStyle = {
    ':hover': {
      backgroundColor: COLOR_WHITE,
    },
  };

  return (
    <>
      <Button
        variant={isFollowed ? 'outline' : 'filled'}
        radius="xl"
        className={cn(
          isFollowed ? 'text-white border-white' : 'bg-white border-2',
          'text-right',
        )}
        styles={isFollowed ? {} : {root: noHoverStyle}}
        leftIcon={
          isLoading ? (
            <Loader
              size="xs"
              color={isFollowed ? COLOR_WHITE : COLOR_AI_GRADIENT_START}
            />
          ) : isFollowed ? (
            <MdCheckCircleOutline color={COLOR_WHITE} size={20} />
          ) : (
            <MdControlPoint color={COLOR_AI_GRADIENT_START} size={20} />
          )
        }
        onClick={async (e) => {
          e.stopPropagation();
          await handleFollowCompany();
        }}
      >
        {isFollowed ? (
          <>
            <span className="text-sm font-bold mr-4 text-white">
              フォロー中
            </span>
            {isSignedIn && (
              <span className="text-sm font-bold text-white">
                {followCount}
              </span>
            )}
          </>
        ) : (
          <>
            <span
              className="text-sm font-bold mr-4 bg-clip-text text-transparent"
              style={gradientTextStyle}
            >
              {customFollowText || 'フォロー'}
            </span>
            {isSignedIn && (
              <span
                className="text-sm font-bold bg-clip-text text-transparent"
                style={gradientTextStyle}
              >
                {followCount}
              </span>
            )}
          </>
        )}
      </Button>
      <NoLoginAlertModal opened={isLoginOpened} onClose={closeLoginModal} />
    </>
  );
};
